/**
 * Sprint 157 — Los periodos que se le ofrecen a la dueña para el reporte de la contadora.
 *
 * Decisión de José (2026-09-26): Mensual, Quincenal, Semanal u Otras fechas, y **cada opción dice
 * los días exactos que cubre** antes de descargar. "Semanal" a secas no dice cuál semana; la
 * contadora recibe fechas, no nombres de opción.
 *
 * Sólo se ofrecen periodos **ya terminados**: un mes a medias mandado a la contadora es un mes que
 * luego no cuadra con el siguiente reporte del mismo mes. Para algo a medias existe "Otras fechas".
 */

export type TipoPeriodo = 'mensual' | 'quincenal' | 'semanal' | 'fechas';

export interface OpcionPeriodo {
  /** Clave estable para el `<select>`. */
  id: string;
  /** Lo que se lee en la lista: "agosto 2026", "16 al 31 de agosto". */
  etiqueta: string;
  desde: string;
  hasta: string;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

export function aTexto(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function ultimoDiaDelMes(anio: number, mes: number): number {
  return new Date(anio, mes + 1, 0).getDate();
}

/** Los últimos 12 meses terminados, el más reciente primero. */
export function mesesTerminados(hoy: Date, cuantos = 12): OpcionPeriodo[] {
  const r: OpcionPeriodo[] = [];
  for (let i = 1; i <= cuantos; i++) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
    const a = d.getFullYear();
    const m = d.getMonth();
    r.push({
      id: `m-${a}-${m}`,
      etiqueta: `${MESES[m]} ${a}`,
      desde: aTexto(new Date(a, m, 1)),
      hasta: aTexto(new Date(a, m, ultimoDiaDelMes(a, m))),
    });
  }
  return r;
}

/** Las últimas quincenas terminadas (1–15 y 16–fin de mes), la más reciente primero. */
export function quincenasTerminadas(hoy: Date, cuantas = 8): OpcionPeriodo[] {
  const r: OpcionPeriodo[] = [];
  // Se recorren quincenas hacia atrás desde la que contiene a `hoy`, que no ha terminado.
  let a = hoy.getFullYear();
  let m = hoy.getMonth();
  let primera = hoy.getDate() <= 15;
  while (r.length < cuantas) {
    // Retroceder una quincena.
    if (primera) {
      m -= 1;
      if (m < 0) {
        m = 11;
        a -= 1;
      }
      primera = false;
    } else {
      primera = true;
    }
    const d1 = primera ? 1 : 16;
    const d2 = primera ? 15 : ultimoDiaDelMes(a, m);
    r.push({
      id: `q-${a}-${m}-${d1}`,
      etiqueta: `${d1} al ${d2} de ${MESES[m]} ${a}`,
      desde: aTexto(new Date(a, m, d1)),
      hasta: aTexto(new Date(a, m, d2)),
    });
  }
  return r;
}

/** Las últimas semanas terminadas, de lunes a domingo, la más reciente primero. */
export function semanasTerminadas(hoy: Date, cuantas = 8): OpcionPeriodo[] {
  const r: OpcionPeriodo[] = [];
  // Lunes de la semana en curso (getDay: 0 = domingo).
  const lunesActual = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  lunesActual.setDate(lunesActual.getDate() - ((lunesActual.getDay() + 6) % 7));
  for (let i = 1; i <= cuantas; i++) {
    const lunes = new Date(lunesActual);
    lunes.setDate(lunes.getDate() - 7 * i);
    const domingo = new Date(lunes);
    domingo.setDate(domingo.getDate() + 6);
    const mismoMes = lunes.getMonth() === domingo.getMonth();
    const etiqueta = mismoMes
      ? `lunes ${lunes.getDate()} al domingo ${domingo.getDate()} de ${MESES[domingo.getMonth()]}`
      : `lunes ${lunes.getDate()} de ${MESES[lunes.getMonth()]} al domingo ${domingo.getDate()} de ${MESES[domingo.getMonth()]}`;
    r.push({ id: `s-${aTexto(lunes)}`, etiqueta, desde: aTexto(lunes), hasta: aTexto(domingo) });
  }
  return r;
}

/** "1 al 31 de agosto de 2026": lo que se lee antes de descargar. Igual que el PDF. */
export function periodoEnPalabras(desde: string, hasta: string): string {
  const [a1, m1, d1] = desde.split('-').map(Number);
  const [a2, m2, d2] = hasta.split('-').map(Number);
  if (desde === hasta) return `${d1} de ${MESES[m1 - 1]} de ${a1}`;
  if (a1 === a2 && m1 === m2) return `${d1} al ${d2} de ${MESES[m2 - 1]} de ${a2}`;
  if (a1 === a2) return `${d1} de ${MESES[m1 - 1]} al ${d2} de ${MESES[m2 - 1]} de ${a2}`;
  return `${d1} de ${MESES[m1 - 1]} de ${a1} al ${d2} de ${MESES[m2 - 1]} de ${a2}`;
}
